import { SessionCtrl } from '~/controllers/session.controller';
import { SessionStore } from '~/stores/session.store';
import { UserStore } from '~/stores/user.store';
import {
    AuthenticatedGlobalContext,
    AuthenticatedGlobalContextValue
} from '~/providers/base-global-context/authenticated-global.context';
import { UiPersistenceStore } from '~/stores/ui-persistence.store';
import { TourStore } from '~/stores/tour.store';
import { MembershipUiStore } from '~/components/Membership/stores/membership.ui.store';
import { IApiService } from '~/services/interfaces/IApiService';
import { MAIN_APP_ROUTER_BASE_PATH } from '~/common/url.constants';
import { ApiService } from '~/services/api.service';
import { SessionService } from '~/services/session.service';
import { UserService } from '~/services/user.service';
import { PicTimeGalleriesStore } from '~/apps/main/Workspace/SidebarTools/PicTime/stores/PicTimeProjectTool.store';
import { TGlobalContextType } from '~/types/global-context.types';
import { BaseSessionStore } from '~/stores/base-session.store';
import {
    IAuthorizedAnalyticsService,
    AnalyticsService
} from '~/services/analytics/analytics.service';
import { ILocalStorage } from '~/services/interfaces/ILocalStorage';
import { StorageService } from '~/services/storage-service';
import { IWebSocketsAdapter } from '~/services/interfaces/IWebSocketsAdapter';
import { WebSocketsAdapter } from '~/services/adapters/web-sockets.adapter';

export interface GlobalMXContextValue extends AuthenticatedGlobalContextValue {
    // Util services
    // injectorService is now inherited from AuthenticatedGlobalContextValue

    // Auth services
    get sessionCtrl(): SessionCtrl;

    get sessionStore(): SessionStore;

    get userStore(): UserStore;

    // UI services
    get uiPersistenceStore(): UiPersistenceStore;

    get tourStore(): TourStore;

    // Api Services
    get sessionService(): SessionService;

    get userService(): UserService;

    get membershipUiStore(): MembershipUiStore;

    get picTimeGalleriesStore(): PicTimeGalleriesStore;
}

export class GlobalMXContext
    extends AuthenticatedGlobalContext
    implements GlobalMXContextValue
{
    // Util services
    // injectorService is now inherited from AuthenticatedGlobalContext

    // Auth services
    sessionService: SessionService;
    sessionCtrl: SessionCtrl;
    get userStore() {
        return this._userStore as UserStore;
    }
    get sessionStore(): SessionStore {
        return this._sessionStore as SessionStore;
    }
    membershipUiStore: MembershipUiStore;
    picTimeGalleriesStore: PicTimeGalleriesStore;

    // UI services
    uiPersistenceStore: UiPersistenceStore;
    tourStore: TourStore;

    // Api Services
    userService: UserService;

    constructor() {
        super();

        // Util services
        // injectorService is initialized in AuthenticatedGlobalContext constructor

        // Auth services
        this.sessionService = new SessionService({
            apiService: this.apiService,
            jStorageService: this.jStorageService
        });
        this.sessionCtrl = new SessionCtrl(this, MAIN_APP_ROUTER_BASE_PATH);
        this.membershipUiStore = new MembershipUiStore(this);
        this.picTimeGalleriesStore = new PicTimeGalleriesStore();

        // Api Services
        this.userService = new UserService({
            apiService: this.apiService,
            configProvider: this.configStore
        });

        // UI services
        this.uiPersistenceStore = new UiPersistenceStore({
            userService: this.userService,
            userStore: this.userStore,
            injectorService: this.injectorService
        });
        this.tourStore = new TourStore({
            uiPersistenceStore: this.uiPersistenceStore
        });
    }

    initApiServices(): IApiService {
        return new ApiService(this);
    }

    initUserStore() {
        return new UserStore(this);
    }

    initSessionStore(): BaseSessionStore {
        return new SessionStore();
    }

    get globalContextType(): TGlobalContextType {
        return 'MX';
    }
}
